import { AxiosError } from 'axios'
import isArray from 'lodash/isArray'
import { action, computed, flow, makeObservable, observable, toJS } from 'mobx'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator, SortDirection, SortingParams } from 'Core/interfaces'
import { TableStore } from 'Core/stores/tableStore/TableStore'
import { getResortedItemsByName, prepareGenericParams, setURLParams } from 'Core/utils'
import { storagePageSizeConstant } from 'Core/utils/storeHelpers/storagePageSizeConstant'

import { STORE_KPI, STORE_LOCATION } from 'Portals/Customer/constants'
import { Kpi, KpiFilterFields, KpiMetricFilterFields } from 'Portals/Customer/entities'
import { KpiRepo, LocationRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'

const STORAGE_BACKUP_DOCUMENT_PAGE_SIZE = storagePageSizeConstant('kpiTable')

export class KpiTable extends TableStore<Kpi.Basic> {
	@observable newItems: Kpi.Basic[] = []
	@observable infinitePageNumber: number = 1
	@observable mobileLocationSorting: SortingParams | null = null
	constructor(private rootStore: RootStore) {
		super(STORAGE_BACKUP_DOCUMENT_PAGE_SIZE)
		makeObservable(this)
	}

	@computed
	get list() {
		return toJS(this.items)
	}

	@computed
	get serviceSiteId() {
		const serviceSiteId = this.filters[KpiFilterFields.serviceSiteId]?.data
		return isArray(serviceSiteId) ? serviceSiteId[0] : serviceSiteId
	}

	@flow.bound
	*getKpiItems(): CustomGenerator<ListData<Kpi.Basic>> {
		if (
			!this.filters[KpiFilterFields.categoryCode]?.data ||
			!this.filters[KpiFilterFields.serviceSiteId]?.data
		) {
			return
		}

		this.rootStore[STORE_KPI].resetKpiConfigs()
		this.isLoading = true
		setURLParams({ searchFilters: this.searchFilters, filters: this.filters })

		try {
			this.infinitePageNumber = 1
			const params = prepareGenericParams(this)
			const { items, total } = yield KpiRepo.getList(params)
			const mappedItems = items.map((val) => ({
				...val,
				rowKey: val.id
			}))
			this.items = mappedItems
			this.totalCount = total
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.isLoading = false
		}
	}

	@flow.bound
	*addKpiItems(): CustomGenerator<ListData<Kpi.Basic>> {
		const newPageNumber = this.infinitePageNumber + 1
		try {
			const params = prepareGenericParams(this)
			const newParams = {
				...params,
				page: newPageNumber
			}
			const { items, total } = yield KpiRepo.getList(newParams)
			const mappedItems = items.map((val) => ({
				...val,
				rowKey: val.id
			}))
			this.newItems = mappedItems
			this.totalCount = total || 0
			this.infinitePageNumber = newPageNumber
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	converMetricData(locData: ListData<Kpi.Metric.ResponseBasic>, kpiData: Kpi.Basic) {
		const { id: kpiId, ...restKpi } = kpiData
		return locData.items.map(({ id, ...rest }) => ({
			...restKpi,
			...rest,
			id: kpiId,
			rowKey: id
		})) as Kpi.Basic[]
	}

	@flow.bound
	*getMetricItems(): CustomGenerator<ListData<Kpi.Metric.ResponseBasic> | Kpi.Basic> {
		if (
			!this.filters[KpiMetricFilterFields.kpiCategoryMetric]?.data ||
			!this.filters[KpiMetricFilterFields.kpiId]?.data
		) {
			return
		}

		setURLParams({ searchFilters: this.searchFilters, filters: this.filters })

		this.rootStore[STORE_KPI].resetKpiConfigs()
		this.rootStore[STORE_KPI].resetDirtyConfig()
		this.isLoading = true

		try {
			this.infinitePageNumber = 1
			const sorting = this.sortingParams
			const params = prepareGenericParams(this)
			const { sortField, sortOrder, ...rest } = params
			const locData = yield LocationRepo.getLocationMetricData(rest)
			const kpiData = yield KpiRepo.getKpiById(
				this.filters[KpiMetricFilterFields.kpiId].data as string
			)
			const correctLocData = locData as ListData<Kpi.Metric.ResponseBasic>
			const data = this.converMetricData(correctLocData, kpiData as Kpi.Basic)
			if (!!sorting) {
				this.onResortLocation(sorting.order, data)
				this.totalCount = correctLocData.total
				return
			}
			this.items = data
			this.totalCount = correctLocData.total
			this.mobileLocationSorting = this.sortingParams
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.isLoading = false
		}
	}

	@flow.bound
	*addKpiMetricItems(): CustomGenerator<ListData<Kpi.Metric.ResponseBasic> | Kpi.Basic> {
		const newPageNumber = this.infinitePageNumber + 1
		try {
			const params = prepareGenericParams(this)
			const { sortField, sortOrder, ...rest } = params
			const newParams = {
				...rest,
				page: newPageNumber
			}
			const locData = yield LocationRepo.getLocationMetricData(newParams)
			const kpiData = yield KpiRepo.getKpiById(
				this.filters[KpiMetricFilterFields.kpiId]?.data as string
			)
			const correctLocData = locData as ListData<Kpi.Metric.ResponseBasic>
			const data = this.converMetricData(correctLocData, kpiData as Kpi.Basic)
			this.newItems = data
			this.totalCount = correctLocData.total || 0
			this.infinitePageNumber = newPageNumber
			this.mobileLocationSorting = this.sortingParams
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@action.bound
	onChangeCategoryFilter() {
		this.setFilter({
			...this.filters[KpiFilterFields.categoryCode],
			data: this.rootStore[STORE_LOCATION].kpiCategoryOptions[0]?.value
		})
	}

	@action.bound
	clearTable() {
		this.items = []
		this.totalCount = 0
		this.newItems = []
		this.clearFilters()
		this.clearSorting()
	}

	@action.bound
	onChangeKpiMetricFilter() {
		this.setFilter({
			...this.filters[KpiMetricFilterFields.kpiId],
			data: this.rootStore[STORE_KPI].kpiOptions[0]?.value
		})
	}

	@action.bound
	onResortLocation(order: SortDirection | undefined, items: Kpi.Basic[]) {
		this.items = getResortedItemsByName(order, items)
	}
}
